import React from "react";
import {
  Clock,
  CheckCircle,
  Wallet,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../components/ui/card";
import { useAuth } from "../hooks/useAuth";
import { useInteractionTracking } from "../hooks/useAnalytics";
import welcomeImg from "../assets/images/welcome-img.svg";
import { useReferrer } from "../hooks/useReferrer";

const WelcomeScreen = ({
  userData,
  onComplete,
  onFullApp,
  onReset,
  isReturningUser,
  partnerName,
}) => {
  const { trackButtonClick } = useInteractionTracking();
  const { displayConfig, isLoading: referrerLoading } = useReferrer();
  partnerName = referrerLoading ? undefined : displayConfig.partnerName;
  //console.log('WelcomeScreen displayConfig:', displayConfig);

  // Journey steps with updated copy
  const steps = [
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Sign up to create your Ownli account",
      description: "Securely verify your identity",
    },
    // Conditionally include the sync step only for DIMO
    ...(displayConfig.enableDimo
      ? [
          {
            icon: <CheckCircle className="w-6 h-6 text-green-500" />,
            title: "Sync optional info from your connected apps",
            description: "Connect your existing data for better offers",
          },
        ]
      : []),
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Take a quick survey",
      description: "Answer a few simple questions about your insurance needs",
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Complete your Ownli Insurance Profile",
      description: "Provide your insurance details",
    },
    {
      icon: <Wallet className="w-6 h-6 text-green-500" />,
      title: "Get matched with insurance buyers and see offers.",
      description: "Receive rewards in your Ownli wallet",
    },
  ];

  // Check if user has existing progress
  const hasSurveyData = sessionStorage.getItem("surveyData");
  const hasUserData = sessionStorage.getItem("userData");
  const hasProgress = hasSurveyData || hasUserData;
  const surveyData = hasSurveyData ? JSON.parse(hasSurveyData) : {};
  const isCompletedSurvey = Object.keys(surveyData).length >= 3;

  return (
    <div className="space-y-8 p-4 pt-5 pb-6">
      {/* Welcome Header with updated copy */}
      <div className="text-center bg-white p-5 rounded-[20px]">
        <h1 className="font-bebasNeue text-3xl font-bold text-gray-900 mb-2">
          {isReturningUser
            ? "Welcome back to Your Insurance Profile Journey"
            : "Your Ownli Insurance Profile"}
        </h1>
        <p className="text-lg text-gray-600">
          {partnerName && partnerName !== "Ownli"
            ? `Powered by ${partnerName} + Ownli — built to get you the right insurance offers`
            : "Built to get you the right insurance offers"}
        </p>
        <img className="mx-auto mt-5" src={welcomeImg} alt="Welcome image" />
      </div>

      {/* Time Commitment */}
      {!isReturningUser ? (
        <Card className="bg-greenish border-none !mt-6 rounded-[20px]">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <Clock className="min-w-[25px] min-h-[25px] text-green" />
              <p className="text-base text-darkGrey">
                Takes less than 5 minutes to complete
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div></div>
      )}

      {/* Returning User Card */}
      {isReturningUser ? (
        <Card className="bg-green-50 border-green-100">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-1" />
              <div>
                <p className="font-medium text-green-900">Welcome Back!</p>
                <p className="text-sm text-green-700">
                  We'll take you straight to your personal information
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="!m-4"></div>
      )}

      {!isReturningUser ? (
        <Card className="bg-white border-none rounded-[20px] !mt-0">
          <CardHeader>
            <CardTitle className="text-base font-bold">
              Here's what to expect:
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {steps.map((step, index) => (
                <div key={index} className="flex items-start gap-3">
                  {/* {step.icon} */}
                  <span className="mt-2 min-w-[10px] min-h-[10px] bg-darkGreen rounded-full"></span>
                  <div>
                    <h3 className="font-base text-darkGrey">{step.title}</h3>
                    {/* <p className="text-sm text-gray-600">{step.description}</p> */}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div></div>
      )}

      {/* CTAs */}
      <div className="space-y-3">
        <button
          onClick={() => {
            trackButtonClick("welcome_get_started", {
              is_returning_user: isReturningUser,
              partner_name: partnerName,
              button_text: isReturningUser
                ? "Continue to Personal Information"
                : "Let's Get Started",
            });
            onComplete();
          }}
          className="w-full flex uppercase font-bold items-center justify-center gap-2 bg-purpleGradient text-white px-4 py-3 rounded-lg hover:bg-purpleGradientDark transition-colors"
        >
          {isReturningUser
            ? "Continue to Personal Information"
            : "Let's Get Started"}

          <ArrowRight className="w-4 h-4" />
        </button>

        {/* Only show skip to verification for new users */}
        {!isReturningUser && isCompletedSurvey && !hasUserData && (
          <button
            onClick={() => {
              trackButtonClick("welcome_skip_to_verification", {
                has_survey_data: isCompletedSurvey,
                has_user_data: !!hasUserData,
              });
              // This will set a flag in session storage that App.js can use to skip to verification
              sessionStorage.setItem("skipToVerification", "true");
              onComplete();
            }}
            className="w-full flex items-center justify-center gap-2 border border-gray-200 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Skip to Verification
          </button>
        )}

        {/* Show reset button if there's existing progress */}
        {/* <button
          onClick={onReset}
          className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-gray-900 text-sm transition-colors"
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          Start Over
        </button> */}

        <div className="text-center text-sm text-gray-600">
          By continuing, you agree to Ownli's{" "}
          <a
            href="https://support.ownli.co/hc/en-us/articles/360011275958-Terms-of-Service"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline inline-flex items-center"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="https://support.ownli.co/hc/en-us/articles/360011276138-Ownli-Privacy-Policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline inline-flex items-center"
          >
            Privacy Policy
          </a>
          .
        </div>
      </div>

      {/* Return User Section (conditionally rendered) */}
      {userData?.returnUser && (
        <Card className="bg-gray-50 border-gray-100">
          <CardContent className="pt-6">
            <p className="text-sm text-gray-600 text-center">
              Welcome back! Continue where you left off or start fresh.
            </p>
            <div className="mt-3 flex justify-center gap-2">
              <button
                onClick={onComplete}
                className="text-blue-600 text-sm hover:text-blue-700"
              >
                Continue
              </button>
              <span className="text-gray-400">|</span>
              <button
                onClick={onReset}
                className="text-blue-600 text-sm hover:text-blue-700"
              >
                Start Over
              </button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WelcomeScreen;
